/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Master Booster Frequently Asked Questions", description = "Increase match rates for your audiences and conversion events in advertising platforms.", lastUpdatedDate = new Date(1752624000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Master Booster Frequently Asked Questions",
        description: "Increase match rates for your audiences and conversion events in advertising platforms.",
        lastUpdatedDate: "Wed Jul 16 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What format should source identifiers use?",
          "depth": 1,
          "id": "what-format-should-source-identifiers-use"
        }, {
          "value": "How should I store multiple identifiers of the same type for one user?",
          "depth": 1,
          "id": "how-should-i-store-multiple-identifiers-of-the-same-type-for-one-user"
        }, {
          "value": "When does enrichment take place? Does Hightouch store a copy of my data?",
          "depth": 1,
          "id": "when-does-enrichment-take-place-does-hightouch-store-a-copy-of-my-data"
        }, {
          "value": "How long does model enrichment take?",
          "depth": 1,
          "id": "how-long-does-model-enrichment-take"
        }, {
          "value": "How often do my boosted models refresh?",
          "depth": 1,
          "id": "how-often-do-my-boosted-models-refresh"
        }, {
          "value": "How can I know the last time my boosted model has been refreshed?",
          "depth": 1,
          "id": "how-can-i-know-the-last-time-my-boosted-model-has-been-refreshed"
        }, {
          "value": "Is there a way to manually refresh my boosting?",
          "depth": 1,
          "id": "is-there-a-way-to-manually-refresh-my-boosting"
        }, {
          "value": "Why do my match rates appear lower with Match Booster?",
          "depth": 1,
          "id": "why-do-my-match-rates-appear-lower-with-match-booster"
        }, {
          "value": "Why is Snapchat reporting greater than 100% match?",
          "depth": 1,
          "id": "why-is-snapchat-reporting-greater-than-100-match"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h3: "h3",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      code: "code",
      em: "em",
      strong: "strong",
      ul: "ul",
      li: "li",
      img: "img"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h3, {
        id: "what-format-should-source-identifiers-use",
        children: "What format should source identifiers use?"
      }), "\n", _jsx(_components.p, {
        children: "Match Booster accepts both raw and hashed (SHA256 or MD5) email identifiers. For hashed emails, the raw values\nneed to be lowercased and trimmed before hashing. However, other PII identifiers need to be in a raw, unhashed format.\nHightouch then normalizes the identifiers—for example, by lowercasing and removing spaces to ensure it matches data in our identity graph."
      }), "\n", _jsx(_components.h3, {
        id: "how-should-i-store-multiple-identifiers-of-the-same-type-for-one-user",
        children: "How should I store multiple identifiers of the same type for one user?"
      }), "\n", _jsx(_components.p, {
        children: "The best way to model multiple identifiers of the same type is to consolidate all values in a single row, and to capture multiple entries as an array.\nIf you have just one value per identifier, a string is appropriate."
      }), "\n", _jsx(_components.p, {
        children: "For example:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "First Name"
            }), _jsx(_components.th, {
              children: "Email"
            }), _jsx(_components.th, {
              children: "Phone"
            }), _jsx(_components.th, {
              children: "uuid"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Tyler"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "“tyler@work.com”"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "[”555-123-4567” , “999-555-1234”]"
              })
            }), _jsx(_components.td, {
              children: "1"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Amina"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "[”amina@work.com”, “amina@gmail.com”]"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "”555-567-8309”"
              })
            }), _jsx(_components.td, {
              children: "2"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch formats the data to match the requirements of ad platform APIs."
      }), "\n", _jsx(_components.h3, {
        id: "when-does-enrichment-take-place-does-hightouch-store-a-copy-of-my-data",
        children: "When does enrichment take place? Does Hightouch store a copy of my data?"
      }), "\n", _jsxs(_components.p, {
        children: ["After you configure match boosting for a model, Hightouch runs a comparison of your selected identifiers against our identity graph and looks for exact matches.\nHightouch uses a hashed version of your model's primary key to keep track of and access matched rows.\n", _jsx(_components.em, {
          children: "Hightouch never stores any of your proprietary data."
        })]
      }), "\n", _jsx(_components.h3, {
        id: "how-long-does-model-enrichment-take",
        children: "How long does model enrichment take?"
      }), "\n", _jsx(_components.p, {
        children: "Once you've configured your boosted model and selected which identifiers to use for enrichment, you can expect enrichment to take a couple of hours and up to one day.\nThis is true for both the first and subsequent enrichments."
      }), "\n", _jsx(_components.h3, {
        id: "how-often-do-my-boosted-models-refresh",
        children: "How often do my boosted models refresh?"
      }), "\n", _jsx(_components.p, {
        children: "You can choose how frequently your boosted models are refreshed. By default, after the first enrichment cycle, Hightouch refreshes boosted models every 14 days."
      }), "\n", _jsx(_components.h3, {
        id: "how-can-i-know-the-last-time-my-boosted-model-has-been-refreshed",
        children: "How can I know the last time my boosted model has been refreshed?"
      }), "\n", _jsxs(_components.p, {
        children: ["You can find the following information in the ", _jsx(_components.strong, {
          children: "Match boosting"
        }), " tab of any boosted model's configuration:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Enrichment Schedule"
        }), "\n", _jsx(_components.li, {
          children: "Last enrichment"
        }), "\n", _jsx(_components.li, {
          children: "Next enrichment"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/match-boosting-details.png",
          alt: "Match boosting details"
        })
      }), "\n", _jsx(_components.h3, {
        id: "is-there-a-way-to-manually-refresh-my-boosting",
        children: "Is there a way to manually refresh my boosting?"
      }), "\n", _jsxs(_components.p, {
        children: ["Yes. Click ", _jsx(_components.strong, {
          children: "Save and run enrichment"
        }), " from the ", _jsx(_components.strong, {
          children: "Match boosting"
        }), " tab in model configuration."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/match-boosting-refresh.png",
          alt: "Match boosting refresh"
        })
      }), "\n", _jsx(_components.h3, {
        id: "why-do-my-match-rates-appear-lower-with-match-booster",
        children: "Why do my match rates appear lower with Match Booster?"
      }), "\n", _jsx(_components.p, {
        children: "Upon the initial sync to an advertising platform, match rates can take up to 48 hours to stabilize depending on the platform.\nAllow enough time for results to calculate."
      }), "\n", _jsx(_components.h3, {
        id: "why-is-snapchat-reporting-greater-than-100-match",
        children: "Why is Snapchat reporting greater than 100% match?"
      }), "\n", _jsxs(_components.p, {
        children: ["Snapchat is unconventional in the way it reports match rate.\nIf you send multiple identifiers for the same user, Snapchat initially reports ", _jsx(_components.em, {
          children: "separate matches"
        }), ", which can lead to a greater than 100% match rate.\nLater, Snapchat deduplicates and resolves multiple matches down to a single user.\nCheck back in a day or two to see the stabilized match rate."]
      })]
    });
  }
}
export default MDXContent;
